import { useMemo } from 'react';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { buildMicrobiomeAgeOverTimeChart } from 'services/microbiome/buildMicrobiomeAgeOverTimeChart';
import {
  MicrobiomeAgeRange,
  MicrobiomeAgeSample,
} from 'services/microbiome/types/microbiome-age.types';
import useMicrobiomeAgeRanges from 'services/microbiome/useMicrobiomeAgeRanges';

import useGetTaxaOverTime from '../../LineChart/hooks/useGetTaxaOverTime';

// Fractional years (not floored like getAgeInYears) so dots land on their true x.
const DAYS_PER_YEAR = 365.25;

// Fetches the evaluation ranges and the kit's samples and folds them into the
// over-time chart model. Returns null when there's nothing to plot.
export default function useMicrobiomeAgeOverTimeModel(metricId: number) {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { data: rangesData } = useMicrobiomeAgeRanges(
    currentKitId ?? '',
    'multi',
  );
  const taxa = useGetTaxaOverTime({ metricId });

  return useMemo(() => {
    const ranges: MicrobiomeAgeRange[] = Array.isArray(rangesData)
      ? rangesData
      : rangesData
        ? [rangesData]
        : [];

    // One sample per historical kit; different-version points arrive filtered out.
    const samples: MicrobiomeAgeSample[] = taxa
      .filter(p => p.user_value != null)
      .map(p => ({
        kitId: p.kit_id,
        age: p.age_at_sampling / DAYS_PER_YEAR,
        microbiomeAge: p.user_value as number,
        evaluationColor: p.evaluation_color,
        sampleDate: p.sample_date,
        isCurrent: p.kit_id === currentKitId,
      }));

    return buildMicrobiomeAgeOverTimeChart(ranges, samples);
  }, [rangesData, taxa, currentKitId]);
}
